import { useContext } from "preact/hooks";
import { Icon } from "../ui/icon";
import { EditContext } from "./edits";
import { interpolate, type Labels } from "./labels";
import { Bar } from "./pop";
import { PortraitBox } from "./portrait_box";
import { useOpenPopup } from "./popups";
import { type LevelUpEntry, type MenuUrls, SheetMenu } from "./sheet_menu";
import type { SheetView } from "./sheet_view";
import { ACTION } from "./styles";

export type Status = "live" | "offline" | "rejected" | "syncing";
type Shown = Status | "saving";

const DOT: Record<Shown, string> = {
	live: "bg-verde-500",
	saving: "bg-dourado-400",
	syncing: "bg-dourado-400",
	offline: "bg-vermelho-500",
	rejected: "bg-vermelho-500",
};

const MAX_LEVEL = 15;

// The XP the character lacks for the next level; 0 when it has enough, or there is no next level.
const xpMissing = ({ sheet }: SheetView) => {
	const next = sheet.experience?.next_level_points;
	return next == null || !sheet.experience ? 0 : Math.max(0, next - sheet.experience.points);
};

const LINK = "font-bold text-link hover:text-link-hover";

// Rolagens for a sheet in a campaign, Oficina for whoever may edit, and the menu.
function Actions({ view, labels, feed, editUrl, menu }: { view: SheetView; labels: Labels; feed: boolean; editUrl?: string; menu: MenuUrls }) {
	const open = useOpenPopup();
	const edits = useContext(EditContext);
	const missing = xpMissing(view);
	// A character below the last level, for a viewer who may edit and has Level Up; the XP it lacks is a hint, never a block.
	const levelUp: LevelUpEntry | null =
		edits && open && view.record_type === "character" && view.sheet.level < MAX_LEVEL
			? {
					hint: missing > 0 ? interpolate(labels.level_up.menu_missing, { missing: missing.toLocaleString(document.documentElement.lang || "pt-BR") }) : null,
					open: (opener) => open({ kind: "level_up" }, opener),
				}
			: null;
	return (
		<div class="cs-head-actions flex flex-none flex-wrap items-center justify-end gap-1.5 self-start">
			{open && feed && (
				<button type="button" class={ACTION} aria-label={labels.header.rolls} onClick={(event) => open({ kind: "rolls" }, event.currentTarget)}>
					<Icon name="rollingDices" class="h-[18px] w-[18px]" />
					<span class="cs-act-label">{labels.header.rolls}</span>
				</button>
			)}
			{edits && editUrl && (
				<a class={ACTION} href={editUrl} aria-label={labels.header.workshop}>
					<Icon name="anvil" class="h-[18px] w-[18px]" />
					<span class="cs-act-label">{labels.header.workshop}</span>
				</a>
			)}
			<SheetMenu urls={menu} labels={labels} levelUp={levelUp} />
		</div>
	);
}

export function Header({
	view,
	status,
	saving,
	labels,
	locale,
	feed,
	editUrl,
	menu,
	picker,
}: {
	view: SheetView;
	status: Status;
	saving: boolean;
	labels: Labels;
	locale: string;
	feed: boolean;
	editUrl?: string;
	menu: MenuUrls;
	// Whether the sheet has the portrait picker (a character's, for whoever may edit it).
	picker: boolean;
}) {
	const { sheet, meta } = view;
	const shown = status === "live" && saving ? "saving" : status;
	const level = view.record_type === "retainer" ? labels.header.retainer_level : interpolate(labels.header.level, { level: sheet.level });
	const traits = [
		sheet.race && (
			<a class={LINK} href={`/racas/${sheet.race.slug}`}>
				{sheet.race.name}
			</a>
		),
		sheet.klass ? (
			<a class={LINK} href={`/classes/${sheet.klass.slug}`}>
				{sheet.klass.name}
			</a>
		) : (
			sheet.profession
		),
		<span class="whitespace-nowrap">{level}</span>,
		sheet.alignment && labels.alignments[sheet.alignment],
	].filter(Boolean);

	return (
		<header class="border-b border-line bg-dourado-100 text-grafite-700 dark:bg-dourado-900 dark:text-ink">
			<div class="cs-head mx-auto max-w-screen-xl px-3 py-3">
				<PortraitBox url={meta.portrait_url} name={sheet.name} labels={labels} picker={picker} />
				<div class="cs-head-text">
					<div class="flex min-w-0 flex-wrap items-center gap-x-3">
						<h1 tabIndex={-1} class="cs-title min-w-0 break-words text-2xl font-bold leading-tight">
							{sheet.name}
						</h1>
						<span role="status" class="inline-flex items-center gap-1.5 text-xs text-grafite-500 dark:text-ink-muted">
							<span class={`h-2 w-2 rounded-full ${DOT[shown]}`} />
							{labels.status[shown]}
						</span>
					</div>
					<p class="cs-break text-base">
						{traits.map((trait, index) => (
							<>
								{index > 0 && (
									<span class="mx-1 text-xs" aria-hidden="true">
										◆
									</span>
								)}
								{index > 0 && <wbr />}
								{trait}
							</>
						))}
						{meta.campaign_id && (
							<span class="cs-head-campaign">
								<span class="mx-1" aria-hidden="true">
									·
								</span>
								<wbr />
								<a class={`inline-flex items-center gap-1 text-sm ${LINK}`} href={`/campanhas/${meta.campaign_id}`}>
									<Icon name="team" class="h-3.5 w-3.5 flex-none" />
									{meta.campaign_title}
								</a>
							</span>
						)}
					</p>
					{sheet.experience && <Experience experience={sheet.experience} labels={labels} locale={locale} character={view.record_type === "character"} />}
				</div>
				<Actions view={view} labels={labels} feed={feed} editUrl={editUrl} menu={menu} />
			</div>
		</header>
	);
}

export function PanelClose({ labels, onClose }: { labels: Labels; onClose: () => void }) {
	return (
		<button
			type="button"
			aria-label={labels.panel.close}
			title={labels.panel.close}
			class="inline-flex h-11 w-11 flex-none items-center justify-center rounded-lg text-ink hover:bg-surface-sunken"
			onClick={onClose}
		>
			<Icon name="close" class="h-3.5 w-3.5" />
		</button>
	);
}

// The header in Mapas' panel: the name and where the sheet stands, the way to the full sheet, and the way out. It sticks,
// so the way out stays in reach however far the sheet is scrolled.
export function PanelHeader({
	view,
	status,
	saving,
	labels,
	sheetUrl,
	onClose,
}: {
	view: SheetView;
	status: Status;
	saving: boolean;
	labels: Labels;
	sheetUrl?: string;
	onClose: () => void;
}) {
	const { sheet } = view;
	const shown = status === "live" && saving ? "saving" : status;
	return (
		<header class="sticky top-0 z-10 flex items-start gap-2 border-b border-line bg-dourado-100 py-2 pl-3 pr-1 text-grafite-700 dark:bg-dourado-900 dark:text-ink">
			<div class="flex min-w-0 flex-1 flex-col gap-0.5">
				<div class="flex items-center gap-2">
					<h2 class="cs-title min-w-0 truncate text-xl font-bold leading-tight">{sheet.name}</h2>
					<span role="status" class="inline-flex flex-none items-center gap-1.5 text-xs text-grafite-500 dark:text-ink-muted">
						<span class={`h-2 w-2 rounded-full ${DOT[shown]}`} />
						{labels.status[shown]}
					</span>
				</div>
				{sheetUrl && (
					<a class={`inline-flex items-center gap-1 text-sm ${LINK}`} href={sheetUrl} target="_blank" rel="noopener">
						<Icon name="externalLink" class="h-3.5 w-3.5" />
						{labels.header.full_sheet}
					</a>
				)}
			</div>
			<PanelClose labels={labels} onClose={onClose} />
		</header>
	);
}

function Experience({
	experience,
	labels,
	locale,
	character,
}: {
	experience: NonNullable<SheetView["sheet"]["experience"]>;
	labels: Labels;
	locale: string;
	character: boolean;
}) {
	const number = (n: number) => n.toLocaleString(locale);
	const next = experience.next_level_points;
	const capped = experience.at_max_level || next == null;
	const edits = useContext(EditContext);
	const open = useOpenPopup();
	const text = capped
		? interpolate(labels.header.xp_max, { points: number(experience.points) })
		: interpolate(labels.header.xp_next, { points: number(experience.points), next: number(next), level: experience.next_level });
	return (
		<div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
			<Icon name="levelUp" class="h-[18px] w-[18px] text-dourado-500" />
			<div class="flex w-40 max-w-full">
				<Bar percent={capped ? 100 : (experience.points / next) * 100} color="bg-azul-500" label={text} track="dark:bg-surface-sunken" />
			</div>
			<span>{text}</span>
			{edits && open && (
				<button
					type="button"
					class="cs-tap inline-flex min-h-8 items-center gap-1 rounded-full border border-verde-600 px-2.5 text-[13px] font-bold text-verde-600 hover:bg-verde-100 dark:border-verde-300 dark:text-verde-300 dark:hover:bg-verde-900"
					data-xp-open
					onClick={(event) => open({ kind: "xp" }, event.currentTarget)}
				>
					{labels.xp.button}
				</button>
			)}
			{edits && open && character && next != null && experience.points >= next && (
				<button
					type="button"
					class="cs-tap inline-flex min-h-8 items-center gap-1 rounded-full bg-verde-600 px-2.5 text-[13px] font-bold text-white hover:brightness-110"
					onClick={(event) => open({ kind: "level_up" }, event.currentTarget)}
				>
					<Icon name="levelUp" class="h-3.5 w-3.5" />
					{interpolate(labels.level_up.header_button, { level: experience.next_level })}
				</button>
			)}
		</div>
	);
}
